import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AlertCircle, Eye, Mail, User, AlertTriangle, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import UserDisplayName from "./UserDisplayName";
import UserDetailDialog from "./UserDetailDialog";
import DeleteUserDialog from "./DeleteUserDialog";
import UsersPagination from "./UsersPagination";
import { formatDate } from "./usersListUtils";
import { TherapistPremiumToggle } from "./TherapistPremiumToggle";

interface UserData {
  id: string;
  user_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  language?: string;
  language_secondary?: string;
  country?: string;
  created_at?: string | null;
  journal_count?: number;
  is_placeholder?: boolean;
  premium?: boolean;
}

interface UsersListProps {
  title: string;
  users: UserData[];
  itemsPerPage?: number;
  onUserDeleted?: () => void;
}

const UsersList = ({ title, users, itemsPerPage = 5, onUserDeleted }: UsersListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [usersList, setUsersList] = useState<UserData[]>(users);

  const totalPages = Math.ceil(usersList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, usersList.length);
  const currentUsers = usersList.slice(startIndex, endIndex);
  const hasData = usersList.length > 0;
  const hasPlaceholders = usersList.some(user => user.is_placeholder);

  const handleViewUserDetails = (user: UserData) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDeleteClick = (user: UserData) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePremiumToggle = (userId: string, isPremium: boolean) => {
    // Update local state to immediately reflect the change in UI
    const updatedUsers = usersList.map(user => {
      if ((user.id || user.user_id) === userId) {
        return { ...user, premium: isPremium };
      }
      return user;
    });
    setUsersList(updatedUsers);
  };

  // Initialize usersList with the users prop when it changes
  React.useEffect(() => {
    setUsersList(users);
  }, [users]);

  return (
    <div className="rounded-md border bg-white shadow-sm w-full">
      <div className="p-4 border-b">
        <h3 className="text-lg font-medium flex items-center">
          <User className="mr-2 h-5 w-5 text-[#333B44]" />
          {title} ({usersList.length})
          {hasPlaceholders && (
            <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">
              Limited Data Access
            </Badge>
          )}
        </h3>
        {hasPlaceholders && (
          <p className="mt-2 text-sm text-amber-600 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            Unable to access complete user details. Showing placeholder data based on analytics.
          </p>
        )}
      </div>
      <div className="overflow-x-auto w-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="hidden sm:table-cell">Country</TableHead>
              <TableHead className="hidden md:table-cell">Languages</TableHead>
              <TableHead className="hidden md:table-cell">Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!hasData ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-[#5A636E]">
                    <AlertCircle className="h-8 w-8 text-amber-500 mb-2" />
                    <p>No users found in the database.</p>
                    <p className="text-sm mt-1">
                      {title.includes("Client") ? 
                        "There seem to be client roles but no detailed data available." : 
                        "There seem to be therapist roles but no detailed data available."}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : currentUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-[#5A636E]">
                    <AlertCircle className="h-8 w-8 text-amber-500 mb-2" />
                    <p>No users matched your filter criteria.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              currentUsers.map((user, index) => (
                <TableRow key={user.id || user.user_id || `user-${index}`}>
                  <TableCell className="font-medium">{startIndex + index + 1}</TableCell>
                  <TableCell>
                    <UserDisplayName user={user} />
                  </TableCell>
                  <TableCell>{user.email || 'Unknown'}</TableCell>
                  <TableCell className="hidden sm:table-cell">{user.country || 'Unknown'}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {user.language || 'Unknown'}
                    {user.language_secondary && (
                      <span className="block text-xs text-muted-foreground">
                        + {user.language_secondary}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{formatDate(user.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end items-center gap-2">
                      {title.toLowerCase().includes('therapist') && (
                        <TherapistPremiumToggle
                          therapistId={user.id || user.user_id || ''}
                          isPremium={user.premium || false}
                          onToggle={(checked) => handlePremiumToggle(user.id || user.user_id || '', checked)}
                        />
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        title="View details"
                        onClick={() => handleViewUserDetails(user)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        title="Send email"
                        onClick={(e) => {
                          e.preventDefault();
                          if (user.email) {
                            const subject = `Regarding Clou user (UID: ${user.id || user.user_id})`;
                            window.location.href = `mailto:${user.email}?subject=${encodeURIComponent(subject)}`;
                          } else {
                            toast.error("Email address not available");
                          }
                        }}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Delete user"
                        onClick={() => handleDeleteClick(user)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      <UsersPagination 
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      <UserDetailDialog 
        user={selectedUser} 
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <DeleteUserDialog
        user={selectedUser}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onUserDeleted={onUserDeleted}
      />
    </div>
  );
};

export default UsersList;
